import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Building2,
	ArrowLeft,
	Users,
	Package,
	Crown,
	Settings,
	Activity,
	Calendar,
	Mail,
	ChevronLeft,
	ChevronRight,
	Check,
	X,
	Trash2,
	AlertTriangle,
	Loader2,
	UserPlus,
	MapPin,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import * as api from "@/services/api";
import { GpsSeatHoldersCard } from "@/components/organizations/GpsSeatHoldersCard";
import { BoundPhonesCard } from "@/components/organizations/BoundPhonesCard";

const planColors: Record<string, string> = {
	free: "bg-gray-100 text-gray-700",
	starter: "bg-blue-100 text-blue-700",
	professional: "bg-purple-100 text-purple-700",
	enterprise: "bg-amber-100 text-amber-700",
};

const statusColors: Record<string, string> = {
	active: "bg-green-100 text-green-700",
	inactive: "bg-gray-100 text-gray-700",
	cancelled: "bg-red-100 text-red-700",
	trialing: "bg-blue-100 text-blue-700",
	past_due: "bg-yellow-100 text-yellow-700",
	incomplete: "bg-orange-100 text-orange-700",
};

const plans = ["free", "starter", "professional", "enterprise"];
const statuses = [
	"active",
	"inactive",
	"cancelled",
	"trialing",
	"past_due",
	"incomplete",
];

export function OrganizationDetail() {
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [usersPage, setUsersPage] = useState(1);
	const [usersSearch, setUsersSearch] = useState("");
	const [editingPlan, setEditingPlan] = useState(false);
	const [editingStatus, setEditingStatus] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState("");
	const [selectedStatus, setSelectedStatus] = useState("");
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [deleteConfirmation, setDeleteConfirmation] = useState("");
	const [showAddUserDialog, setShowAddUserDialog] = useState(false);
	const [newUser, setNewUser] = useState({
		email: "",
		firstName: "",
		lastName: "",
		password: "",
		role: "employee" as "employee" | "manager" | "admin" | "owner",
		employmentType: "" as "" | "PERMANENT" | "CONTRACT" | "INTERN",
	});
	const [purchasedSeatsInput, setPurchasedSeatsInput] = useState("");
	const [attendanceSeatsInput, setAttendanceSeatsInput] = useState("");

	const { data: org, isLoading } = useQuery({
		queryKey: ["admin-organization", id],
		queryFn: () => api.getAdminOrganization(id!),
		enabled: !!id,
	});

	const { data: stats } = useQuery({
		queryKey: ["admin-organization-stats", id],
		queryFn: () => api.getOrganizationDetailStats(id!),
		enabled: !!id,
	});

	const { data: usersData } = useQuery({
		queryKey: ["admin-organization-users", id, usersPage, usersSearch],
		queryFn: () =>
			api.getOrganizationUsers(id!, {
				page: usersPage,
				limit: 10,
				search: usersSearch || undefined,
			}),
		enabled: !!id,
	});

	const updatePlanMutation = useMutation({
		mutationFn: (plan: string) => api.updateOrganizationPlan(id!, plan),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-stats", id],
			});
			setEditingPlan(false);
		},
	});

	const updateStatusMutation = useMutation({
		mutationFn: (status: string) => api.updateOrganizationStatus(id!, status),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-stats", id],
			});
			setEditingStatus(false);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: () => api.deleteAdminOrganization(id!),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
			navigate("/organizations");
		},
	});

	const createUserMutation = useMutation({
		mutationFn: (data: api.CreateOrganizationUserPayload) =>
			api.createOrganizationUser(id!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-users", id],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-stats", id],
			});
			setShowAddUserDialog(false);
			setNewUser({
				email: "",
				firstName: "",
				lastName: "",
				password: "",
				role: "employee",
				employmentType: "",
			});
		},
	});

	const updatePurchasedSeatsMutation = useMutation({
		mutationFn: (purchasedSeats: number) =>
			api.updateAdminOrganization(id!, { purchasedSeats }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
		},
	});

	const { data: attendanceSeats } = useQuery({
		queryKey: ["admin-organization-attendance-seats", id],
		queryFn: () => api.getAttendanceSeats(id!),
		enabled: !!id,
	});

	/*
	 * Both seat inputs are seeded with the real value rather than showing it as
	 * a placeholder.
	 *
	 * A placeholder leaves the input genuinely empty, and an empty number input
	 * has no value to step from — so clicking the spinner arrows on a field
	 * "showing" 10 produced 1, not 11. The value has to be in the input, not
	 * behind it.
	 *
	 * Keyed on the server value, so typing is never clobbered mid-edit: it
	 * re-syncs only when the saved figure actually changes.
	 */
	const currentPurchasedSeats = org?.purchasedSeats ?? 0;
	const currentAttendanceSeats = attendanceSeats?.purchased ?? 0;

	useEffect(() => {
		setPurchasedSeatsInput(String(currentPurchasedSeats));
	}, [currentPurchasedSeats]);

	useEffect(() => {
		setAttendanceSeatsInput(String(currentAttendanceSeats));
	}, [currentAttendanceSeats]);

	const updateAttendanceSeatsMutation = useMutation({
		mutationFn: (purchased: number) =>
			api.updateAttendanceSeats(id!, purchased),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin-organization-attendance-seats", id],
			});
		},
	});

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
			</div>
		);
	}

	if (!org) {
		return (
			<div className="text-center py-12">
				<p className="text-gray-500">Organization not found</p>
				<Link to="/organizations">
					<Button variant="outline" className="mt-4">
						<ArrowLeft className="w-4 h-4 mr-2" />
						Back to Organizations
					</Button>
				</Link>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Link to="/organizations">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="w-4 h-4 mr-1" />
						Back
					</Button>
				</Link>
				<div className="flex-1">
					<div className="flex items-center gap-3">
						<div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
							<Building2 className="w-6 h-6 text-gray-500" />
						</div>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">{org.name}</h1>
							<p className="text-gray-500">{org.slug}</p>
						</div>
					</div>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
					onClick={() => setShowDeleteDialog(true)}
				>
					<Trash2 className="w-4 h-4 mr-1" />
					Delete Organization
				</Button>
			</div>

			{/* Delete Confirmation Dialog */}
			{showDeleteDialog && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50"
						onClick={() => {
							if (!deleteMutation.isPending) {
								setShowDeleteDialog(false);
								setDeleteConfirmation("");
							}
						}}
					/>
					<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
						<div className="flex items-center gap-3 mb-4">
							<div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
								<AlertTriangle className="w-5 h-5 text-red-600" />
							</div>
							<div>
								<h3 className="text-lg font-semibold text-gray-900">
									Delete Organization
								</h3>
								<p className="text-sm text-gray-500">
									This action cannot be undone
								</p>
							</div>
						</div>

						<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
							<p className="text-sm text-red-800">
								<strong>Warning:</strong> This will permanently delete the
								organization <strong>"{org.name}"</strong> and ALL associated
								data including:
							</p>
							<ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
								<li>All users and their accounts</li>
								<li>All HRIS data (payroll, departments, shifts)</li>
								<li>All attendance records</li>
								<li>All monitoring data and screenshots</li>
								<li>All KPI and appraisal data</li>
								<li>All roles and permissions</li>
							</ul>
						</div>

						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Type <strong>{org.name}</strong> to confirm:
							</label>
							<Input
								value={deleteConfirmation}
								onChange={(e) => setDeleteConfirmation(e.target.value)}
								placeholder="Enter organization name"
								disabled={deleteMutation.isPending}
								className="w-full"
							/>
						</div>

						{deleteMutation.isError && (
							<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
								<p className="text-sm text-red-800">
									{deleteMutation.error instanceof Error
										? deleteMutation.error.message
										: "Failed to delete organization"}
								</p>
							</div>
						)}

						<div className="flex justify-end gap-3">
							<Button
								variant="outline"
								onClick={() => {
									setShowDeleteDialog(false);
									setDeleteConfirmation("");
								}}
								disabled={deleteMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={() => deleteMutation.mutate()}
								disabled={
									deleteConfirmation !== org.name || deleteMutation.isPending
								}
								className="bg-red-600 hover:bg-red-700"
							>
								{deleteMutation.isPending ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										Deleting...
									</>
								) : (
									<>
										<Trash2 className="w-4 h-4 mr-2" />
										Delete Organization
									</>
								)}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Add User Dialog */}
			{showAddUserDialog && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50"
						onClick={() => {
							if (!createUserMutation.isPending) {
								setShowAddUserDialog(false);
								setNewUser({
									email: "",
									firstName: "",
									lastName: "",
									password: "",
									role: "employee",
									employmentType: "",
								});
							}
						}}
					/>
					<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
						<div className="flex items-center gap-3 mb-4">
							<div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
								<UserPlus className="w-5 h-5 text-blue-600" />
							</div>
							<div>
								<h3 className="text-lg font-semibold text-gray-900">
									Add User
								</h3>
								<p className="text-sm text-gray-500">
									Create a new user in this organization
								</p>
							</div>
						</div>

						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Email <span className="text-red-500">*</span>
								</label>
								<Input
									type="email"
									value={newUser.email}
									onChange={(e) =>
										setNewUser({ ...newUser, email: e.target.value })
									}
									placeholder="user@example.com"
									disabled={createUserMutation.isPending}
								/>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										First Name <span className="text-red-500">*</span>
									</label>
									<Input
										value={newUser.firstName}
										onChange={(e) =>
											setNewUser({ ...newUser, firstName: e.target.value })
										}
										placeholder="John"
										disabled={createUserMutation.isPending}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Last Name
									</label>
									<Input
										value={newUser.lastName}
										onChange={(e) =>
											setNewUser({ ...newUser, lastName: e.target.value })
										}
										placeholder="Doe"
										disabled={createUserMutation.isPending}
									/>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Password <span className="text-red-500">*</span>
								</label>
								<Input
									type="password"
									value={newUser.password}
									onChange={(e) =>
										setNewUser({ ...newUser, password: e.target.value })
									}
									placeholder="Min 6 characters"
									disabled={createUserMutation.isPending}
								/>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Role
									</label>
									<select
										value={newUser.role}
										onChange={(e) =>
											setNewUser({
												...newUser,
												role: e.target.value as
													| "employee"
													| "manager"
													| "admin"
													| "owner",
											})
										}
										className="w-full px-3 py-2 border rounded-md text-sm"
										disabled={createUserMutation.isPending}
									>
										<option value="employee">Employee</option>
										<option value="manager">Manager</option>
										<option value="admin">Admin</option>
										<option value="owner">Owner</option>
									</select>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Employment Type
									</label>
									<select
										value={newUser.employmentType}
										onChange={(e) =>
											setNewUser({
												...newUser,
												employmentType: e.target.value as
													| ""
													| "PERMANENT"
													| "CONTRACT"
													| "INTERN",
											})
										}
										className="w-full px-3 py-2 border rounded-md text-sm"
										disabled={createUserMutation.isPending}
									>
										<option value="">Not specified</option>
										<option value="PERMANENT">Permanent</option>
										<option value="CONTRACT">Contract</option>
										<option value="INTERN">Intern</option>
									</select>
								</div>
							</div>
						</div>

						{createUserMutation.isError && (
							<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
								<p className="text-sm text-red-800">
									{createUserMutation.error instanceof Error
										? createUserMutation.error.message
										: "Failed to create user"}
								</p>
							</div>
						)}

						<div className="flex justify-end gap-3 mt-6">
							<Button
								variant="outline"
								onClick={() => {
									setShowAddUserDialog(false);
									setNewUser({
										email: "",
										firstName: "",
										lastName: "",
										password: "",
										role: "employee",
										employmentType: "",
									});
								}}
								disabled={createUserMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								onClick={() => {
									createUserMutation.mutate({
										email: newUser.email,
										firstName: newUser.firstName,
										lastName: newUser.lastName || undefined,
										password: newUser.password,
										role: newUser.role,
										employmentType: newUser.employmentType || undefined,
									});
								}}
								disabled={
									!newUser.email ||
									!newUser.firstName ||
									!newUser.password ||
									newUser.password.length < 6 ||
									createUserMutation.isPending
								}
								className="bg-blue-600 hover:bg-blue-700"
							>
								{createUserMutation.isPending ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										Creating...
									</>
								) : (
									<>
										<UserPlus className="w-4 h-4 mr-2" />
										Add User
									</>
								)}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Quick Stats */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-blue-100 rounded-lg">
								<Users className="w-5 h-5 text-blue-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{stats?.users.total || 0}</p>
								<p className="text-sm text-gray-500">Total Users</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-green-100 rounded-lg">
								<Activity className="w-5 h-5 text-green-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{stats?.users.active || 0}</p>
								<p className="text-sm text-gray-500">Active Users</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-purple-100 rounded-lg">
								<Crown className="w-5 h-5 text-purple-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">
									{stats?.users.inPayroll || 0}
								</p>
								<p className="text-sm text-gray-500">In Payroll</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-amber-100 rounded-lg">
								<Calendar className="w-5 h-5 text-amber-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">
									{stats?.users.recentlyAdded || 0}
								</p>
								<p className="text-sm text-gray-500">Added (30d)</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Subscription Info */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Package className="w-5 h-5" />
							Subscription
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Plan */}
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Plan
							</label>
							{editingPlan ? (
								<div className="flex items-center gap-2">
									<select
										value={selectedPlan}
										onChange={(e) => setSelectedPlan(e.target.value)}
										className="flex-1 px-3 py-2 border rounded-md text-sm"
									>
										{plans.map((plan) => (
											<option key={plan} value={plan}>
												{plan.charAt(0).toUpperCase() + plan.slice(1)}
											</option>
										))}
									</select>
									<Button
										size="sm"
										onClick={() => updatePlanMutation.mutate(selectedPlan)}
										disabled={updatePlanMutation.isPending}
									>
										<Check className="w-4 h-4" />
									</Button>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => setEditingPlan(false)}
									>
										<X className="w-4 h-4" />
									</Button>
								</div>
							) : (
								<div className="flex items-center justify-between">
									<Badge className={planColors[org.plan] || ""}>{org.plan}</Badge>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => {
											setSelectedPlan(org.plan);
											setEditingPlan(true);
										}}
									>
										Change
									</Button>
								</div>
							)}
						</div>

						{/* Status */}
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Status
							</label>
							{editingStatus ? (
								<div className="flex items-center gap-2">
									<select
										value={selectedStatus}
										onChange={(e) => setSelectedStatus(e.target.value)}
										className="flex-1 px-3 py-2 border rounded-md text-sm"
									>
										{statuses.map((status) => (
											<option key={status} value={status}>
												{status
													.split("_")
													.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
													.join(" ")}
											</option>
										))}
									</select>
									<Button
										size="sm"
										onClick={() => updateStatusMutation.mutate(selectedStatus)}
										disabled={updateStatusMutation.isPending}
									>
										<Check className="w-4 h-4" />
									</Button>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => setEditingStatus(false)}
									>
										<X className="w-4 h-4" />
									</Button>
								</div>
							) : (
								<div className="flex items-center justify-between">
									<Badge
										className={
											statusColors[org.subscriptionStatus || "inactive"] || ""
										}
									>
										{(org.subscriptionStatus || "inactive")
											.split("_")
											.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
											.join(" ")}
									</Badge>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => {
											setSelectedStatus(org.subscriptionStatus || "inactive");
											setEditingStatus(true);
										}}
									>
										Change
									</Button>
								</div>
							)}
						</div>

						{/* Period End */}
						{org.subscriptionCurrentPeriodEnd && (
							<div>
								<label className="block text-sm font-medium text-gray-500 mb-1">
									Period Ends
								</label>
								<p className="text-sm">
									{new Date(
										org.subscriptionCurrentPeriodEnd,
									).toLocaleDateString()}
								</p>
							</div>
						)}

						{/* Created */}
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Created
							</label>
							<p className="text-sm">
								{org.createdAt
									? new Date(org.createdAt).toLocaleDateString()
									: "-"}
							</p>
						</div>

						{/* Seat Management */}
						{(() => {
							const usedSeats = org.seatCount ?? 0;
							const purchasedSeats = org.purchasedSeats ?? 0;
							const activeUsers = stats?.users.active ?? usedSeats;
							const deactivatedUsers = stats?.users.inactive ?? 0;
							const availableSeats = Math.max(purchasedSeats - usedSeats, 0);
							const usagePercent = purchasedSeats > 0 ? (usedSeats / purchasedSeats) * 100 : 0;
							const activePercent = purchasedSeats > 0 ? (activeUsers / purchasedSeats) * 100 : 0;
							const deactivatedPercent = purchasedSeats > 0 ? (deactivatedUsers / purchasedSeats) * 100 : 0;
							const canAdminOverride = org.plan === "free" || org.customPackage?.enabled || !org.stripeSubscriptionId;

							return (
								<div className="space-y-3">
									<label className="block text-sm font-medium text-gray-500">
										Seat Usage
									</label>
									<div className="grid grid-cols-2 gap-2">
										<div className="p-2 bg-blue-50 rounded-lg text-center">
											<p className="text-lg font-bold text-blue-700">{purchasedSeats}</p>
											<p className="text-xs text-blue-600">Purchased</p>
										</div>
										<div className="p-2 bg-green-50 rounded-lg text-center">
											<p className="text-lg font-bold text-green-700">{activeUsers}</p>
											<p className="text-xs text-green-600">Active</p>
										</div>
										<div className="p-2 bg-amber-50 rounded-lg text-center">
											<p className="text-lg font-bold text-amber-700">{deactivatedUsers}</p>
											<p className="text-xs text-amber-600">Deactivated</p>
										</div>
										<div className="p-2 bg-gray-50 rounded-lg text-center">
											<p className="text-lg font-bold text-gray-700">{availableSeats}</p>
											<p className="text-xs text-gray-600">Available</p>
										</div>
									</div>

									{/* Usage Progress Bar */}
									{purchasedSeats > 0 && (
										<div>
											<div className="flex justify-between text-xs text-gray-500 mb-1">
												<span>{usedSeats} / {purchasedSeats} seats used</span>
												<span>{Math.round(usagePercent)}%</span>
											</div>
											<div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex">
												<div
													className="h-full bg-blue-500 transition-all"
													style={{ width: `${Math.min(activePercent, 100)}%` }}
												/>
												<div
													className="h-full bg-amber-400 transition-all"
													style={{ width: `${Math.min(deactivatedPercent, 100 - activePercent)}%` }}
												/>
											</div>
											<div className="flex gap-3 mt-1">
												<span className="flex items-center gap-1 text-xs text-gray-500">
													<span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
													Active
												</span>
												<span className="flex items-center gap-1 text-xs text-gray-500">
													<span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
													Deactivated
												</span>
												<span className="flex items-center gap-1 text-xs text-gray-500">
													<span className="w-2 h-2 rounded-full bg-gray-200 inline-block" />
													Available
												</span>
											</div>
										</div>
									)}

									{/* Pending Reduction Notice */}
									{org.scheduledSeatReduction && (
										<div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
											<p className="text-xs text-yellow-800">
												<AlertTriangle className="w-3 h-3 inline mr-1" />
												Seats will reduce from{" "}
												<strong>{purchasedSeats}</strong> to{" "}
												<strong>{org.scheduledSeatReduction.newSeatCount}</strong> on{" "}
												{new Date(org.scheduledSeatReduction.effectiveDate).toLocaleDateString()}
											</p>
										</div>
									)}

									{/*
									  Laid out like the Attendance Seats card next to it, deliberately.
									  This was a ghost button reading "Override Purchased Seats" that had
									  to be clicked before an input appeared. At text-xs in grey it read as
									  a caption, not a control, and was reported as the feature being
									  missing — while the identically-purposed control one card over was
									  found immediately, because that one is a labelled input and a Save
									  button. Two patterns for the same job in one view is what taught the
									  reader that this half was read-only.
									*/}
									{canAdminOverride && (
										<div className="border-t pt-3">
											<label className="block text-sm font-medium text-gray-500 mb-1">
												Set purchased seats
											</label>
											<div className="flex items-center gap-2">
												<Input
													type="number"
													min={usedSeats}
													value={purchasedSeatsInput}
													onChange={(e) => setPurchasedSeatsInput(e.target.value)}
													className="w-24 text-sm"
												/>
												<Button
													size="sm"
													onClick={() => {
														const val = parseInt(purchasedSeatsInput, 10);
														if (!isNaN(val) && val >= usedSeats) {
															updatePurchasedSeatsMutation.mutate(val);
														}
													}}
													disabled={
														updatePurchasedSeatsMutation.isPending ||
														purchasedSeatsInput === "" ||
														parseInt(purchasedSeatsInput, 10) < usedSeats ||
														parseInt(purchasedSeatsInput, 10) === purchasedSeats
													}
												>
													{updatePurchasedSeatsMutation.isPending ? (
														<Loader2 className="w-4 h-4 animate-spin" />
													) : (
														"Save"
													)}
												</Button>
											</div>
											<p className="text-xs text-gray-500 mt-1">
												Controls how many users this organization can have.
												{usedSeats > 0 && ` Cannot go below ${usedSeats} — the seats already in use.`}
											</p>
											{updatePurchasedSeatsMutation.isError && (
												<p className="text-xs text-red-600 mt-1">
													{updatePurchasedSeatsMutation.error instanceof Error
														? updatePurchasedSeatsMutation.error.message
														: "Failed to update seats"}
												</p>
											)}
										</div>
									)}
								</div>
							);
						})()}

						{/* Stripe Customer ID */}
						{org.stripeCustomerId && (
							<div>
								<label className="block text-sm font-medium text-gray-500 mb-1">
									Stripe Customer ID
								</label>
								<p className="text-xs font-mono text-gray-600">
									{org.stripeCustomerId}
								</p>
							</div>
						)}

						{/* Stripe Subscription ID */}
						{org.stripeSubscriptionId && (
							<div>
								<label className="block text-sm font-medium text-gray-500 mb-1">
									Stripe Subscription ID
								</label>
								<p className="text-xs font-mono text-gray-600">
									{org.stripeSubscriptionId}
								</p>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Custom Package */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center justify-between gap-2">
							<span className="flex items-center gap-2">
								<Settings className="w-5 h-5" />
								Custom Package
							</span>
							{/*
							  This card only ever displayed. Everything it shows is editable
							  on the Custom Packages screen, but nothing here said so and
							  nothing linked there, so the card read as the whole feature and
							  "there is no way to change it" was the reasonable conclusion.
							  The link carries the organization, so the editor opens on this
							  one instead of an empty picker.
							*/}
							<Link
								to={`/packages?org=${id}`}
								className="text-sm font-normal text-blue-600 hover:underline"
							>
								{org.customPackage?.enabled ? "Manage" : "Set up"}
							</Link>
						</CardTitle>
					</CardHeader>
					<CardContent>
						{org.customPackage?.enabled ? (
							<div className="space-y-3">
								{org.customPackage.customFeatures &&
									org.customPackage.customFeatures.length > 0 && (
										<div>
											<label className="block text-sm font-medium text-gray-500 mb-1">
												Custom Features
											</label>
											<div className="flex flex-wrap gap-1">
												{org.customPackage.customFeatures
													.slice(0, 5)
													.map((f) => (
														<Badge
															key={f}
															className="bg-green-100 text-green-700"
														>
															+{f}
														</Badge>
													))}
												{org.customPackage.customFeatures.length > 5 && (
													<Badge className="bg-gray-100 text-gray-700">
														+{org.customPackage.customFeatures.length - 5} more
													</Badge>
												)}
											</div>
										</div>
									)}
								{org.customPackage.blockedFeatures &&
									org.customPackage.blockedFeatures.length > 0 && (
										<div>
											<label className="block text-sm font-medium text-gray-500 mb-1">
												Blocked Features
											</label>
											<div className="flex flex-wrap gap-1">
												{org.customPackage.blockedFeatures
													.slice(0, 5)
													.map((f) => (
														<Badge
															key={f}
															className="bg-red-100 text-red-700"
														>
															-{f}
														</Badge>
													))}
												{org.customPackage.blockedFeatures.length > 5 && (
													<Badge className="bg-gray-100 text-gray-700">
														+{org.customPackage.blockedFeatures.length - 5} more
													</Badge>
												)}
											</div>
										</div>
									)}
								{org.customPackage.notes && (
									<div>
										<label className="block text-sm font-medium text-gray-500 mb-1">
											Notes
										</label>
										<p className="text-sm text-gray-600">
											{org.customPackage.notes}
										</p>
									</div>
								)}
							</div>
						) : (
							<p className="text-sm text-gray-500">
								No custom package configured
							</p>
						)}
					</CardContent>
				</Card>

				{/* Attendance Seats (GPS add-on, manual/comped billing) */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<MapPin className="w-5 h-5" />
							Attendance Seats
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-sm text-gray-600">
							GPS-attendance add-on seats. Set manually when comping or billing
							an org outside Stripe.
						</p>
						<div className="grid grid-cols-2 gap-2">
							<div className="p-2 bg-green-50 rounded-lg text-center">
								<p className="text-lg font-bold text-green-700">
									{attendanceSeats?.assigned ?? 0}
								</p>
								<p className="text-xs text-green-600">Assigned</p>
							</div>
							<div className="p-2 bg-blue-50 rounded-lg text-center">
								<p className="text-lg font-bold text-blue-700">
									{attendanceSeats?.purchased ?? 0}
								</p>
								<p className="text-xs text-blue-600">Purchased</p>
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Set purchased seats
							</label>
							<div className="flex items-center gap-2">
								<Input
									type="number"
									min={0}
									value={attendanceSeatsInput}
									onChange={(e) => setAttendanceSeatsInput(e.target.value)}
									className="w-24 text-sm"
								/>
								<Button
									size="sm"
									onClick={() => {
										const val = parseInt(attendanceSeatsInput, 10);
										if (!isNaN(val) && val >= 0) {
											updateAttendanceSeatsMutation.mutate(val);
										}
									}}
									disabled={
										updateAttendanceSeatsMutation.isPending ||
										attendanceSeatsInput === "" ||
										parseInt(attendanceSeatsInput, 10) < 0 ||
										parseInt(attendanceSeatsInput, 10) ===
											(attendanceSeats?.purchased ?? 0)
									}
								>
									{updateAttendanceSeatsMutation.isPending ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										"Save"
									)}
								</Button>
							</div>
							{updateAttendanceSeatsMutation.isError && (
								<p className="text-xs text-red-600 mt-1">
									{updateAttendanceSeatsMutation.error instanceof Error
										? updateAttendanceSeatsMutation.error.message
										: "Failed to update attendance seats"}
								</p>
							)}
							{updateAttendanceSeatsMutation.isSuccess &&
								!updateAttendanceSeatsMutation.isPending && (
									<p className="text-xs text-green-600 mt-1">
										Attendance seats updated.
									</p>
								)}
						</div>
					</CardContent>
				</Card>

				{/* GPS seat holders: 30-day lock + bound phone (FlowMetrics-only releases) */}
				<GpsSeatHoldersCard orgId={id!} />
				<BoundPhonesCard orgId={id!} />

				{/* Employment Types */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Users className="w-5 h-5" />
							User Breakdown
						</CardTitle>
					</CardHeader>
					<CardContent>
						{stats?.employmentTypes &&
						Object.keys(stats.employmentTypes).length > 0 ? (
							<div className="space-y-2">
								{Object.entries(stats.employmentTypes).map(([type, count]) => (
									<div
										key={type}
										className="flex items-center justify-between py-1"
									>
										<span className="text-sm text-gray-600">{type}</span>
										<span className="font-medium">{count}</span>
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500">No user data available</p>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Users Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span className="flex items-center gap-2">
							<Users className="w-5 h-5" />
							Users
						</span>
						<div className="flex items-center gap-2">
							<Input
								placeholder="Search users..."
								value={usersSearch}
								onChange={(e) => {
									setUsersSearch(e.target.value);
									setUsersPage(1);
								}}
								className="w-64"
							/>
							<Button
								size="sm"
								onClick={() => setShowAddUserDialog(true)}
								className="bg-blue-600 hover:bg-blue-700"
							>
								<UserPlus className="w-4 h-4 mr-1" />
								Add User
							</Button>
						</div>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{!usersData ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-6 w-6 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : usersData.users.length === 0 ? (
						<p className="text-center py-8 text-gray-500">No users found</p>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b">
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												User
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Status
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Type
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Created
											</th>
										</tr>
									</thead>
									<tbody>
										{usersData.users.map((user: any) => (
											<tr
												key={user._id}
												className="border-b last:border-0 hover:bg-gray-50"
											>
												<td className="py-3 px-4">
													<div className="flex items-center gap-3">
														<div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
															<Mail className="w-4 h-4 text-gray-400" />
														</div>
														<div>
															<p className="font-medium text-gray-900">
																{user.name ||
																	user.firstName ||
																	user.email.split("@")[0]}
															</p>
															<p className="text-sm text-gray-500">
																{user.email}
															</p>
														</div>
													</div>
												</td>
												<td className="py-3 px-4">
													<Badge
														className={cn(
															user.isActive
																? "bg-green-100 text-green-700"
																: "bg-gray-100 text-gray-700",
														)}
													>
														{user.isActive ? "Active" : "Inactive"}
													</Badge>
												</td>
												<td className="py-3 px-4 text-sm text-gray-600">
													{user.employmentType || "-"}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{user.createdAt
														? new Date(user.createdAt).toLocaleDateString()
														: "-"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{usersData.pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {usersData.pagination.page} of{" "}
										{usersData.pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={usersPage <= 1}
											onClick={() => setUsersPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={usersPage >= usersData.pagination.pages}
											onClick={() => setUsersPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
